import { dxtToLocalServerTime, ResponseMESF } from "@dexteel/mesf-core";
import { Checkbox } from "@mui/material";
import { ColDef, ColGroupDef, ICellRendererParams } from "ag-grid-enterprise";
import moment from "moment";
import { ManualAutoButton } from "../../../../../../../shared/data-entries/ManualAutoButton";
import { useDataEntryContext } from "../../../../context/reducer.provider";
import { Strand } from "../../../../models/caster.models";
import { EditAndPLCButtons } from "../../components/EditAndPLCButtons";
import { SpeedStatus } from "../../components/SpeedStatus";
import { LossDetailsHeader } from "../LossDetailsHeader";
import { StrandLossDetailsCell } from "../StrandLossDetailsCell";

const EditableCheckboxRenderer = ({
  strand,
  value,
  onChange,
  attributeName,
}: {
  strand: Strand;
  value: boolean;
  onChange: (checked: boolean, strand: Strand, key: keyof Strand) => void;
  attributeName: keyof Strand;
}) => {
  const {
    state: { isDataLoading },
  } = useDataEntryContext();

  return (
    <Checkbox
      color="primary"
      checked={value}
      onChange={(e, checked) => {
        onChange(checked, strand, attributeName);
      }}
      disabled={isDataLoading}
    />
  );
};

interface Props {
  editCheckbox: (
    checked: boolean,
    strand: Strand,
    attributeName: keyof Strand,
  ) => void;
  editStrand: (strandId: number) => void;
  readAutomaticStrands: (
    value?: string,
    strandNumber?: number,
  ) => Promise<ResponseMESF>;
  openLossDetails: (strand: Strand) => void;
  canJustifyAllLosses: boolean;
  isJustifyingAllLosses: boolean;
  justifyAllLosses: () => void;
}

export const useTableData = ({
  editCheckbox,
  editStrand,
  readAutomaticStrands,
  openLossDetails,
  canJustifyAllLosses,
  isJustifyingAllLosses,
  justifyAllLosses,
}: Props) => {
  const {
    state: { deProcess },
  } = useDataEntryContext();

  const heatStatusCode = deProcess?.process?.HeatStatusCode;

  const cellStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };
  const columns: ColGroupDef[] = [
    {
      headerName: "Strands",
      children: [
        {
          field: "IsManual",
          headerName: " ",
          flex: 1,
          cellRenderer: ManualAutoButton,
          cellRendererParams: (params: ICellRendererParams) => ({
            isManual: (params.data as Strand).IsManual,
          }),
          cellStyle: cellStyle,
          minWidth: 40,
        },
        {
          field: "Strand",
          headerName: "Strand",
          flex: 1,
          cellStyle: cellStyle,
          minWidth: 60,
        },
        {
          field: "MoldAndSize",
          headerName: "Mold",
          flex: 2,
          cellStyle: cellStyle,
          minWidth: 120,
        },
        {
          field: "HeatsOnMold",
          headerName: "Heats On Mold",
          flex: 1,
          cellStyle: cellStyle,
          minWidth: 60,
          wrapHeaderText: true,
          autoHeaderHeight: true,
        },
        {
          field: "ShroudingMold",
          headerName: "Shrouding Mold",
          flex: 1,
          cellRenderer: EditableCheckboxRenderer,
          cellRendererParams: (params: ICellRendererParams) => ({
            strand: params.data as Strand,
            value: (params.data as Strand).ShroudingMold,
            attributeName: "ShroudingMold",
            onChange: editCheckbox,
          }),
          cellStyle: cellStyle,
          minWidth: 70,
          wrapHeaderText: true,
          autoHeaderHeight: true,
        },
        {
          field: "IsForSale",
          headerName: "Is For Sale",
          flex: 1,
          cellRenderer: EditableCheckboxRenderer,
          cellRendererParams: (params: ICellRendererParams) => ({
            strand: params.data as Strand,
            value: (params.data as Strand).IsForSale,
            attributeName: "IsForSale",
            onChange: editCheckbox,
          }),
          cellStyle: cellStyle,
          minWidth: 60,
        },
        {
          colId: "lossDetails",
          headerComponent: () => (
            <LossDetailsHeader
              canJustifyAll={canJustifyAllLosses}
              isJustifying={isJustifyingAllLosses}
              onJustifyAll={justifyAllLosses}
            />
          ),
          minWidth: 240,
          cellRenderer: StrandLossDetailsCell,
          cellRendererParams: (params: ICellRendererParams<Strand>) => ({
            strand: params.data,
            onOpenLossDetails: openLossDetails,
          }),
          cellStyle: cellStyle,
        },
        {
          field: "Billets",
          headerName: "Billets",
          flex: 1,
          cellStyle: cellStyle,
          minWidth: 60,
        },
        {
          field: "WaterDeltaTAvg",
          headerName: "Water Delta",
          flex: 1,
          cellStyle: cellStyle,
          minWidth: 60,
          wrapHeaderText: true,
          autoHeaderHeight: true,
        },
        {
          field: "WaterFlowAvg",
          headerName: "Water Flow",
          flex: 1,
          cellStyle: cellStyle,
          minWidth: 60,
          wrapHeaderText: true,
          autoHeaderHeight: true,
        },
        {
          field: "WaterTempAvg",
          headerName: "Water Temp",
          flex: 1,
          cellStyle: cellStyle,
          minWidth: 60,
          wrapHeaderText: true,
          autoHeaderHeight: true,
        },
        {
          field: "FirstCutTime",
          headerName: "First Cut",
          flex: 1,
          valueGetter: (params: any) => {
            const firstCutTime = params.data.FirstCutTime;
            if (firstCutTime && moment(firstCutTime).isValid()) {
              return dxtToLocalServerTime(firstCutTime, "HH:mm");
            }
          },
          cellStyle: cellStyle,
          minWidth: 60,
        },
        {
          field: "LastCutTime",
          headerName: "Last Cut",
          flex: 1,
          valueGetter: (params: any) => {
            const lastCutTime = params.data.LastCutTime;
            if (lastCutTime && moment(lastCutTime).isValid()) {
              return dxtToLocalServerTime(lastCutTime, "HH:mm");
            }
          },
          cellStyle: cellStyle,
          minWidth: 60,
        },
        {
          field: "SpeedIPMAvg",
          headerName: "Speed",
          flex: 1,
          minWidth: 70,
          cellStyle: cellStyle,
          cellRenderer: (params: ICellRendererParams) => {
            const speed = params.value;
            if (heatStatusCode === "F") {
              return <span>{speed}</span>;
            }

            const status = SpeedStatus(params);
            return (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-around",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <span
                  style={{
                    minWidth: "30px",
                    textAlign: "right",
                    marginRight: "8px",
                  }}
                >
                  {speed}
                </span>
                <span>{status}</span>
              </div>
            );
          },
        },
        {
          field: "actions",
          headerName: "",
          flex: 2,
          cellRenderer: EditAndPLCButtons,
          cellRendererParams: (params: ICellRendererParams) => ({
            onEdit: editStrand,
            id: (params.data as Strand).Strand,
            readFromPLC: (value: string) =>
              readAutomaticStrands(value, (params.data as Strand).Strand),
            heatStatusCode: heatStatusCode,
          }),
          cellStyle: cellStyle,
          minWidth: 100,
        },
      ],
    },
  ];

  return { columns };
};
